import { useTranslation } from 'react-i18next'
import { AlertTriangle } from 'lucide-react'
import { usePageSEO } from '@/hooks/usePageSEO'
import {
  LegalPageLayout,
  LegalSection,
  LegalList,
  LegalCallout,
  type LegalSectionItem,
} from '@/components/legal/LegalPageLayout'

const SECTION_KEYS = [
  'companyData',
  'siteOwner',
  'copyright',
  'liability',
  'externalLinks',
  'governingLaw',
  'modifications',
  'contact',
] as const

// Legal-notice sections that have an `items` bullet list.
const SECTIONS_WITH_ITEMS = ['copyright', 'liability'] as const

const COMPANY_FIELDS = [
  'legalName',
  'registeredOffice',
  'vat',
  'fiscalCode',
  'rea',
  'shareCapital',
  'pec',
  'email',
  'phone',
] as const

export default function LegalNoticePage() {
  const { t } = useTranslation()

  usePageSEO({
    title: t('meta.legalNotice.title'),
    description: t('meta.legalNotice.description'),
    ogTitle: t('meta.legalNotice.ogTitle'),
    ogDescription: t('meta.legalNotice.ogDescription'),
  })

  const toc: LegalSectionItem[] = SECTION_KEYS.map((key) => ({
    id: `legal-${key}`,
    number: t(`legal.legalNotice.sections.${key}.number`),
    title: t(`legal.legalNotice.sections.${key}.title`),
  }))

  return (
    <LegalPageLayout
      documentNumber="03 · LGL"
      eyebrow={t('legal.legalNotice.eyebrow')}
      title={t('legalNotice.title')}
      subtitle={t('legal.legalNotice.headerSubtitle')}
      intro={t('legal.legalNotice.intro')}
      toc={toc}
    >
      {/* 01 — Company data (specialized block with technical spec-sheet styling) */}
      <LegalSection
        id="legal-companyData"
        number={t('legal.legalNotice.sections.companyData.number')}
        title={t('legal.legalNotice.sections.companyData.title')}
        body={t('legal.legalNotice.sections.companyData.body')}
      >
        <CompanyDataSheet />
        <LegalCallout
          label={t('legal.common.documentLabel')}
          tone="warning"
        >
          <div className="flex items-start gap-3">
            <AlertTriangle
              className="h-4 w-4 text-brand-500 shrink-0 mt-0.5"
              strokeWidth={2.25}
              aria-hidden="true"
            />
            <span>
              {t('legal.legalNotice.sections.companyData.placeholderNote')}
            </span>
          </div>
        </LegalCallout>
      </LegalSection>

      {/* Remaining sections */}
      {SECTION_KEYS.filter((k) => k !== 'companyData').map((key) => {
        const id = `legal-${key}`
        const number = t(`legal.legalNotice.sections.${key}.number`)
        const title = t(`legal.legalNotice.sections.${key}.title`)
        const body = t(`legal.legalNotice.sections.${key}.body`)
        const hasItems = (SECTIONS_WITH_ITEMS as readonly string[]).includes(key)

        const items = hasItems
          ? (t(`legal.legalNotice.sections.${key}.items`, {
              returnObjects: true,
            }) as string[])
          : null

        return (
          <LegalSection
            key={key}
            id={id}
            number={number}
            title={title}
            body={body}
          >
            {items && Array.isArray(items) && <LegalList items={items} />}
          </LegalSection>
        )
      })}
    </LegalPageLayout>
  )
}

/**
 * CompanyDataSheet — mimics a machine-shop data plate / spec sheet.
 * Two-column key→value definition list with sharp corners, monospace
 * values for codes/numbers, and an "info block" header that looks
 * like an engineering label.
 */
function CompanyDataSheet() {
  const { t } = useTranslation()
  // Identify fields whose value should render in monospace (codes/numbers).
  const monoFields = new Set<string>([
    'vat',
    'fiscalCode',
    'rea',
    'pec',
    'email',
    'phone',
  ])
  // Fields that still need to be filled in — flagged visually.
  const placeholderFields = new Set<string>(['rea', 'shareCapital', 'pec'])

  return (
    <div className="mt-6 max-w-3xl relative bg-white border border-ink-200">
      <span
        aria-hidden="true"
        className="absolute top-0 left-0 right-0 h-[3px] bg-brand-500"
      />
      <header className="flex items-center justify-between px-5 py-3.5 border-b border-ink-200 bg-ink-50">
        <p className="font-display font-bold text-[10px] uppercase tracking-[0.22em] text-ink-700">
          {t('legal.common.documentLabel')} · FENIX / DATA-SHEET
        </p>
        <p className="font-mono text-[10px] tracking-wide text-ink-500">
          REV {t('legal.common.versionValue')}
        </p>
      </header>

      <dl className="divide-y divide-ink-200">
        {COMPANY_FIELDS.map((field) => {
          const labelKey = `legal.legalNotice.sections.companyData.fields.${field}`
          const valueKey = `legal.legalNotice.sections.companyData.fields.${field}Value`
          const isMono = monoFields.has(field)
          const isPlaceholder = placeholderFields.has(field)

          return (
            <div
              key={field}
              className="grid grid-cols-1 md:grid-cols-12 gap-2 md:gap-4 px-5 py-3.5"
            >
              <dt className="md:col-span-5 text-[11px] font-bold uppercase tracking-[0.18em] text-ink-500 self-center flex items-center gap-2.5">
                <span
                  aria-hidden="true"
                  className="block h-[2px] w-4 bg-brand-500 shrink-0"
                />
                {t(labelKey)}
              </dt>
              <dd
                className={`md:col-span-7 text-[14px] md:text-[15px] text-ink-900 break-words ${
                  isMono ? 'font-mono tracking-tight' : 'font-semibold'
                } ${isPlaceholder ? 'text-ink-500 italic' : ''}`}
              >
                {t(valueKey)}
              </dd>
            </div>
          )
        })}
      </dl>
    </div>
  )
}
